import { computed } from 'vue'
import { useJwtStore, useNotifyStore } from '@/stores'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { isClient, safeLocalStorage } from '@/composables/useSSR'
import { STATUS_CODES } from '@/constants'
import { useRouter } from 'vue-router'
import { uuid } from '@/composables'
import { useI18n } from 'vue-i18n'

const ADMIN_TOKEN_KEY = 'admin_original_jwt'

interface ImpersonateResponse {
  token: string
}

export function useImpersonation() {
  const jwtStore = useJwtStore()
  const router = useRouter()
  const notifyStore = useNotifyStore()
  const { t } = useI18n()

  const isImpersonating = computed(() => {
    try {
      const content = jwtStore.getJwtContent
      return content?.is_impersonation === true
    } catch {
      return false
    }
  })

  const impersonatedUser = computed(() => {
    if (!isImpersonating.value) return null
    try {
      return jwtStore.getJwtContent
    } catch {
      return null
    }
  })

  const hasStoredAdminToken = computed(() => {
    if (!isClient) return false
    const storage = safeLocalStorage()
    return storage?.getItem(ADMIN_TOKEN_KEY) !== null
  })

  function storeAdminToken(token: string): void {
    const storage = safeLocalStorage()
    if (storage) {
      storage.setItem(ADMIN_TOKEN_KEY, token)
    }
  }

  function getStoredAdminToken(): string | null {
    const storage = safeLocalStorage()
    return storage?.getItem(ADMIN_TOKEN_KEY) ?? null
  }

  function clearStoredAdminToken(): void {
    const storage = safeLocalStorage()
    if (storage) {
      storage.removeItem(ADMIN_TOKEN_KEY)
    }
  }

  async function startImpersonation(userId: number): Promise<boolean> {
    const currentToken = jwtStore.getJwt
    if (!currentToken) {
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: t('common.error'),
        message: t('dashboard.impersonation.error.noToken')
      })
      return false
    }

    try {
      const res = await useAxios<null, ImpersonateResponse>(
        'POST',
        `/admin/impersonate/${userId}`,
        true
      )

      if (res && res.status === STATUS_CODES.OK && res.data?.token) {
        storeAdminToken(currentToken)
        jwtStore.setJwt(res.data.token)

        notifyStore.createNotify({
          id: uuid(),
          alertType: 'success',
          title: t('dashboard.impersonation.started.title'),
          message: t('dashboard.impersonation.started.message')
        })

        router.push({ name: 'dashboardOverview' })
        return true
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'useImpersonation:start' })
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(error)
      })
    }

    return false
  }

  async function endImpersonation(): Promise<boolean> {
    const originalToken = getStoredAdminToken()

    try {
      await useAxios<null, null>('POST', '/admin/impersonate/end', true)

      if (originalToken) {
        jwtStore.setJwt(originalToken)
        clearStoredAdminToken()
      } else {
        jwtStore.removeJwt()
        router.push({ name: 'login' })
        return true
      }

      notifyStore.createNotify({
        id: uuid(),
        alertType: 'success',
        title: t('dashboard.impersonation.ended.title'),
        message: t('dashboard.impersonation.ended.message')
      })

      router.push({ name: 'dashboardAdminUserManagement' })
      return true
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'useImpersonation:end' })
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(error)
      })

      if (originalToken) {
        jwtStore.setJwt(originalToken)
        clearStoredAdminToken()
        router.push({ name: 'dashboardAdminUserManagement' })
        return true
      }
    }

    return false
  }

  return {
    isImpersonating,
    impersonatedUser,
    hasStoredAdminToken,
    startImpersonation,
    endImpersonation
  }
}
